/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import LoadingIndicator from "../../components/LoadingIndicator";
import Text from "../../components/ui/Text";
import Stack from "../../components/ui/Stack";
import Button from "../../components/ui/Button";
import Paginator from "../../components/ui/Paginator";
import Dimmed from "../../components/ui/Dimmed";
import ClientInvoiceItem from "../../components/client/ClientInvoiceItem";
import CancelRecurringModal from "../../components/membership/CancelRecurringModal";
import FormatDateTime from "../../components/formatters/FormatDateTime";
import { RECURRING_PROFILE_STATUS, RECURRING_PROFILE_STATUS_COLOR } from "../../constants/constants";
import { getRecurringProfileDates } from "../../utils/helpers/recurringProfile";
import { useRecurringProfile } from "../../lib/react-query/useRecurringProfile";
import { useRecurringProfileInvoices } from "../../lib/react-query/useRecurringProfileInvoices";
import Badge from "../../components/ui/Badge";
import Icon from "../../components/ui/Icon";
import { RecordCard, RecordCardHeader, RecordCardHeaderCol, TimezoneNote } from "../../components/ui/RecordCard";

const PaginatorWrap = styled.div`
  margin-top: 1rem;
`;

const CancelButtonWrap = styled.div`
  margin-top: 2rem;
`;

const InvoiceList = styled(Stack)`
  padding-inline: 1rem;
`;

const HeaderItem = ({ label, children }) => (
  <Stack $gap="0.25rem">
    <Text size="small">{label}</Text>
    {children}
  </Stack>
);

const ClientRecurringProfile = () => {
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);

  const { data: profile, isLoading } = useRecurringProfile(id);
  const { data: invoicesData, isFetching: invoicesFetching, isPlaceholderData: invoicesPlaceholder } = useRecurringProfileInvoices(id, currentPage);

  const invoices = useMemo(() => invoicesData?.data || [], [invoicesData]);
  const totalPages = useMemo(() => invoicesData?.metadata?.pages_count || 1, [invoicesData]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const isActive = profile?.status === RECURRING_PROFILE_STATUS.ACTIVE;
  const dateRows = useMemo(() => getRecurringProfileDates(profile), [profile]);

  return (
    <Container>
      <NavigationHeader title="view_recurring_profile" back={-1} />

      {isLoading && <LoadingIndicator page />}

      {!isLoading && profile && (
        <>
          <RecordCard>
            <RecordCardHeader>
              <RecordCardHeaderCol $gap="1.5rem">
                <HeaderItem label="invoice_payment_processor">
                  <Text color="dark" weight="bold">
                    {profile.payment_processor}
                  </Text>
                </HeaderItem>
                <HeaderItem label="recurring_profile_status">
                  <Badge
                    title={`recurring_profile_status_${profile.status}`}
                    color={RECURRING_PROFILE_STATUS_COLOR[profile.status]}
                  />
                </HeaderItem>
              </RecordCardHeaderCol>
              <RecordCardHeaderCol $align="right" $gap="1.5rem">
                {dateRows.map(({ labelKey, value }) => (
                  <HeaderItem key={labelKey} label={labelKey}>
                    <Text color="dark" weight="bold">
                      <FormatDateTime value={value} format={FormatDateTime.FORMAT.DATE} />
                    </Text>
                  </HeaderItem>
                ))}
              </RecordCardHeaderCol>
            </RecordCardHeader>

            {invoices.length > 0 && (
              <Dimmed $active={invoicesPlaceholder}>
                <InvoiceList $gap="0.5rem">
                  {invoices.map((invoice) => (
                    <ClientInvoiceItem key={invoice.id} invoice={invoice} />
                  ))}
                </InvoiceList>
              </Dimmed>
            )}
          </RecordCard>

          {invoices.length > 0 && totalPages > 1 && (
            <PaginatorWrap>
              <Paginator
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={invoicesFetching}
              />
            </PaginatorWrap>
          )}

          <TimezoneNote />

          {isActive && (
            <CancelButtonWrap>
              <CancelRecurringModal
                recurringProfileId={id}
                trigger={<Button variant="primary" title="cancel_profile" icon={<Icon.Cross />} />}
              />
            </CancelButtonWrap>
          )}
        </>
      )}
    </Container>
  );
};

export default ClientRecurringProfile;
